import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/currencies';

type Transaction = {
  id: string;
  type: string;
  amount: number;
  status: string;
  created_at: string;
  admin_note: string | null;
  currency: string;
};

export default function TransactionHistory({ userId, showAll = false }: { userId: string; showAll?: boolean }) {
  const { t, language } = useLanguage();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();

    const channel = supabase
      .channel('transactions_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'transactions',
        filter: `user_id=eq.${userId}`
      }, () => {
        fetchTransactions();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const fetchTransactions = async () => {
    try {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!showAll) {
        query = query.limit(5);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('history')}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-muted-foreground">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">No transactions yet</div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">{transaction.type.replace('_', ' ').toUpperCase()}</div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(transaction.created_at), 'PPp')}
                  </div>
                  {transaction.admin_note && (
                    <div className="text-sm text-muted-foreground italic">{transaction.admin_note}</div>
                  )}
                </div>
                <div className="text-right space-y-2">
                  <div className="text-lg font-bold">
                    {formatCurrency(transaction.amount, transaction.currency || 'USD', language)}
                  </div>
                  <Badge className={getStatusColor(transaction.status)}>
                    {t(transaction.status)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}